<template>
  <v-app>
    <v-progress-linear
      indeterminate
      color="primary"
      v-if="!ready || initializingData"
    ></v-progress-linear>
    <v-expand-transition>
      <div v-if="authorized && !initializingData">
        <v-app-bar id="mainNav" app :height="appbarHeight" class="white">
          <div class="d-flex align-center">
            <img
              alt="Academic Development Institute"
              class="shrink mr-2"
              contain
              src="@/assets/adi_logo.png"
              transition="scale-transition"
              :width="logoWidth"
            />
          </div>
          <div class="font-weight-bold primary--text">
            <div>Academic Development Institute</div>
            <div class="text-h6">SURVEY APP</div>
          </div>
          <v-spacer></v-spacer>
          <div class="mx-4 d-flex align-center">
            <v-select
              id="tenantSelect"
              v-model="tenantId"
              rounded
              filled
              dense
              :items="tenantOptions"
              hide-details
              style="max-width: 250px"
              class="mx-2"
            ></v-select>
            <v-divider vertical class="mx-3"></v-divider>
            <v-btn
              text
              :to="{ name: 'surveys' }"
              color="primary"
              :disabled="!hasTenant"
              >Surveys</v-btn
            >
            <v-btn
              text
              :to="{ name: 'entities' }"
              color="primary"
              :disabled="!hasTenant"
              >Entities</v-btn
            >

            <v-menu offset-y bottom left>
              <template v-slot:activator="menu">
                <v-tooltip bottom>
                  <template v-slot:activator="tooltip">
                    <v-btn
                      text
                      v-on="{ ...menu.on, ...tooltip.on }"
                      v-bind="{ ...menu.attrs, ...tooltip.attrs }"
                      color="primary"
                      :class="{
                        'grey lighten-2': userMenu.some(
                          (x) => x.to.name === $route.name
                        ),
                      }"
                      :disabled="!hasTenant"
                      >users</v-btn
                    >
                  </template>

                  <span>Configure App</span>
                </v-tooltip>
              </template>

              <v-list>
                <v-list-item
                  v-for="(item, i) in userMenu"
                  :key="i"
                  link
                  :to="item.to"
                >
                  <v-list-item-title>{{ item.label }}</v-list-item-title>
                </v-list-item>
              </v-list>
            </v-menu>

            <v-btn
              text
              href="#"
              :to="{ name: 'portal', params: { tenantId } }"
              target="blank"
              color="primary"
              :disabled="!hasTenant"
              >Portal</v-btn
            >
          </div>
          <v-btn elevation="0" color="primary" @click="logout">
            Logout
            <font-awesome-icon
              class="ml-2"
              icon="sign-out-alt"
            ></font-awesome-icon>
          </v-btn>
        </v-app-bar>
      </div>
    </v-expand-transition>
    <v-main ref="main" class="grey lighten-2">
      <v-scroll-y-reverse-transition mode="out-in">
        <router-view v-if="authorized && !initializingData" />
      </v-scroll-y-reverse-transition>
    </v-main>
    <login v-if="ready"></login>
    <notifications></notifications>
  </v-app>
</template>

<style lang="scss">
#mainNav {
  &,
  .v-toolbar__content,
  img {
    transition: all 200ms ease-in-out !important;
  }
}

.v-main__wrap {
  display: flex;
  flex-direction: column;
}
</style>

<script>
import Notifications from "../../components/Notifications.vue";
import localStorageService from "../../services/localStorageService";
import login from "./login.vue";

import { roles } from "../../constants/Identity.js";

export default {
  components: { login, Notifications },
  head: {
    title: {
      inner: "ADI Surveys",
      complement: " ",
      separator: " ",
    },
  },
  data: () => ({
    pageYOffset: 0,
    shrinkThreshold: 70,
    ready: false,
    initializingData: false,
  }),
  computed: {
    appbarHeight() {
      return this.pageYOffset < this.shrinkThreshold ? 90 : 70;
    },
    logoWidth() {
      return this.pageYOffset < this.shrinkThreshold ? 77 : 48;
    },
    authorized() {
      return this.$store.getters.authorized;
    },
    user() {
      return this.$store.state.user;
    },
    tenantOptions() {
      return this.$store.state.tenants.data.map((x) => ({
        text: x.name,
        value: x.id,
      }));
    },
    tenantId: {
      get() {
        return this.$store.state.tenantId;
      },
      set(val) {
        this.$store.commit("setTenantId", val);
        localStorageService.setTenantId(val);
      },
    },
    hasTenant() {
      return this.tenantId && this.tenantId != "";
    },
    userMenu() {
      return [
        {
          to: { name: "admins" },
          label: "Server Admins",
        },
        {
          to: { name: "tenant-users" },
          label: "Tenant Users",
        },
      ];
    },
  },
  watch: {
    authorized(val) {
      if (val) {
        if (this.user.role != roles.admin)
          this.$router.replace({ name: "accessDenied" });

        this.initializingData = true;

        if (this.$route.path === "/") this.$router.replace({ name: "surveys" });

        let promises = [];

        promises.push(this.$store.dispatch("tenants/loadAll"));

        Promise.all(promises).then(
          () => {
            // Initialize TenantId
            let tenantId = localStorageService.getTenantId();
            if (
              !this.$store.state.tenants.data.some((x) => x.id === tenantId)
            ) {
              tenantId = this.$store.state.tenants.data[0].id;
            }
            this.tenantId = tenantId;

            this.initializingData = false;
          },
          (error) => {
            console.warn(error);
            this.$store.dispatch(
              "notifications/error",
              "An unexpected error occurred while trying to load app data. Please try again later."
            );
          }
        );
      }
    },
    tenantId(val, prev) {
      if (val != prev) {
        this.initializingData = true;
        this.$store
          .dispatch("tenants/load", val)
          .finally(() => (this.initializingData = false));
      }
    },
  },
  methods: {
    handleScroll() {
      this.pageYOffset = window.pageYOffset;
    },
    logout() {
      return this.$store.dispatch("logout");
    },
  },
  created() {
    window.addEventListener("scroll", this.handleScroll);
  },
  destroyed() {
    window.removeEventListener("scroll", this.handleScroll);
  },
  mounted() {
    this.pageYOffset = window.pageYOffset;
    this.$store.commit("setTheme", "adi");

    let favicon = document.querySelector("#favicon");
    favicon.href = "favicon.ico";
    this.$store.dispatch("refreshAuth").finally(() => (this.ready = true));
  },
};
</script>
